/**
 * useTracking — React hook for fetching and mutating tracking data.
 *
 * Fetches from GET /me/tracking and exposes confirm/reject mutations for
 * ReplanProposal actions.
 *
 * Design:
 *   - Uses getIdToken() from AuthContext — NEVER stores the token in state.
 *   - fetch is called once on mount; confirm/reject trigger a re-fetch afterward.
 *   - Loading starts true; becomes false on first load (success or failure).
 *   - Each section's state is derived from the single /me/tracking response.
 *   - No PII is logged.
 *   - No dangerouslySetInnerHTML usage.
 *
 * Security:
 *   - Authorization header is set to "Bearer {idToken}"; token is fresh per call.
 *   - proposalId is taken directly from the ReplanProposal object — never from
 *     user input or URL params (the hook is called from trusted component props).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { FoodLogEntry, EvolutionPoint, MealPlan, ReplanProposal } from "@autodieta/shared";
import type { AdherenceResult } from "./AdherenceCard.js";
import { useAuth } from "../auth/AuthContext.js";

// ---------------------------------------------------------------------------
// Response type from GET /me/tracking
// ---------------------------------------------------------------------------

interface TrackingResponse {
  foodLogRecent: FoodLogEntry[];
  evolutionPoints: EvolutionPoint[];
  adherence: AdherenceResult;
  activePlan: MealPlan | null;
  pendingProposal: ReplanProposal | null;
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseTrackingResult {
  /** True while the initial fetch (or a refresh) is in progress. */
  loading: boolean;
  /** Error message if the last fetch failed. Undefined on success. */
  error?: string;
  /** Recent food log entries (last 7 days). */
  foodLogRecent?: FoodLogEntry[];
  /** Evolution time series (last 30 days). */
  evolutionPoints?: EvolutionPoint[];
  /** Today's adherence result vs the active plan. */
  adherence?: AdherenceResult;
  /** The user's most recent active MealPlan, or null if none. */
  activePlan?: MealPlan;
  /** A pending ReplanProposal, if one exists (status="proposed"). */
  pendingProposal?: ReplanProposal;
  /** Confirm a proposal — fires POST /me/adjustments/:id/confirm then refreshes. */
  confirm: (proposalId: string) => Promise<void>;
  /** Reject a proposal — fires POST /me/adjustments/:id/reject then refreshes. */
  reject: (proposalId: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// useTracking
// ---------------------------------------------------------------------------

/**
 * Fetches the tracking snapshot from the worker and provides confirm/reject
 * mutations for ReplanProposal actions.
 *
 * @param workerUrl - Base URL of the Cloudflare Worker (e.g. "https://worker.example.com").
 */
export function useTracking(workerUrl: string): UseTrackingResult {
  const { getIdToken } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [data, setData] = useState<TrackingResponse | undefined>(undefined);

  // Stable ref to avoid re-creating fetchData on every render
  const workerUrlRef = useRef(workerUrl);
  workerUrlRef.current = workerUrl;

  const fetchData = useCallback(async () => {
    const token = await getIdToken();
    if (!token) {
      // Not authenticated — skip fetch, set loading=false
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${workerUrlRef.current}/me/tracking`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        // Distinguish real server errors from "no data" (200 with empty sections).
        // A 401 means the session expired; any other non-ok status is a server error.
        const userMessage =
          res.status === 401
            ? "Tu sesión expiró. Por favor, volvé a iniciar sesión."
            : "No se pudieron cargar los datos de seguimiento. Intentá de nuevo más tarde.";
        setError(userMessage);
        setData(undefined);
      } else {
        const body = (await res.json()) as TrackingResponse;
        setData(body);
        setError(undefined);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error";
      setError(message);
      setData(undefined);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const confirm = useCallback(
    async (proposalId: string) => {
      const token = await getIdToken();
      if (!token) return;

      await fetch(`${workerUrlRef.current}/me/adjustments/${proposalId}/confirm`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      // Refresh tracking data after confirm
      setLoading(true);
      await fetchData();
    },
    [getIdToken, fetchData],
  );

  const reject = useCallback(
    async (proposalId: string) => {
      const token = await getIdToken();
      if (!token) return;

      await fetch(`${workerUrlRef.current}/me/adjustments/${proposalId}/reject`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      // Refresh tracking data after reject
      setLoading(true);
      await fetchData();
    },
    [getIdToken, fetchData],
  );

  // exactOptionalPropertyTypes: use conditional spread for optional fields
  return {
    loading,
    confirm,
    reject,
    ...(error !== undefined ? { error } : {}),
    ...(data?.foodLogRecent !== undefined ? { foodLogRecent: data.foodLogRecent } : {}),
    ...(data?.evolutionPoints !== undefined ? { evolutionPoints: data.evolutionPoints } : {}),
    ...(data?.adherence !== undefined ? { adherence: data.adherence } : {}),
    ...(data?.activePlan != null ? { activePlan: data.activePlan } : {}),
    ...(data?.pendingProposal != null ? { pendingProposal: data.pendingProposal } : {}),
  };
}
